import {
  FleetAssetCapabilityRequestDTO,
  Trailer,
  useAddCapabilitiesToTrailerMutation,
  useCreateTrailerMutation,
  useDeleteTrailerMutation,
  useRemoveCapabilitiesFromTrailerMutation,
  useUpdateTrailerMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseTrailerMutationProps = {}

export type UseTrailerMutationResult = {
  createTrailer: (trailer: Partial<Trailer>, callback: EntityMutationCallback<Trailer> | undefined) => void
  updateTrailer: (trailer: Partial<Trailer>, callback: EntityMutationCallback<Trailer> | undefined) => void
  deleteTrailer: (trailerId: string, callback: EntityMutationCallback | undefined) => void
  addCapabilities: (trailerId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => void
  removeCapabilities: (trailerId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isAddingCapabilities?: boolean
  isRemovingCapabilities?: boolean
}

export const useTrailerMutation = (props: UseTrailerMutationProps = {}): UseTrailerMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)
  const [_addingCapabilities, _setAddingCapabilities] = useDebouncedState(false, 500)
  const [_removingCapabilities, _setRemovingCapabilities] = useDebouncedState(false, 500)

  // possible mutations
  const [_createTrailer, {isLoading: isCreating}] = useCreateTrailerMutation()
  const [_updateTrailer, {isLoading: isUpdating}] = useUpdateTrailerMutation()
  const [_deleteTrailer, {isLoading: isDeleting}] = useDeleteTrailerMutation()
  const [_addCapabilities, {isLoading: isAddingCapabilities}] = useAddCapabilitiesToTrailerMutation()
  const [_removeCapabilities, {isLoading: isRemovingCapabilities}] = useRemoveCapabilitiesFromTrailerMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setAddingCapabilities(isAddingCapabilities)
  }, [isAddingCapabilities, _setAddingCapabilities])

  useEffect(() => {
    _setRemovingCapabilities(isRemovingCapabilities)
  }, [isRemovingCapabilities, _setRemovingCapabilities])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useTrailerUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.trailer", "Trailer"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTrailerUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating || _addingCapabilities || _removingCapabilities) {
      notifications.show({
        id: "useTrailerUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.trailer", "Trailer"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTrailerUpdate_updating")
    }
  }, [_updating, _addingCapabilities, _removingCapabilities, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useTrailerUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.trailer", "Trailer"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTrailerUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Trailer>, callback: EntityMutationCallback<Trailer> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.trailer", "Trailer") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<Trailer> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.trailer", "Trailer") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.trailer", "Trailer") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((trailerId: string, callback: EntityMutationCallback | undefined) => {
    const promise = _deleteTrailer({id: trailerId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteTrailer, handleDeleteResult])

  const createTrailer = useCallback((trailer: Partial<Trailer>, callback: EntityMutationCallback<Trailer> | undefined) => {
    const promise = _createTrailer(trailer).unwrap()
    handleCreateResult(promise, callback)
  }, [_createTrailer, handleCreateResult])

  const updateTrailer = useCallback((trailer: Partial<Trailer>, callback: EntityMutationCallback<Trailer> | undefined) => {
    const promise = _updateTrailer(trailer).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateTrailer, handleUpdateResult])

  const deleteTrailer = useCallback((trailerId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.trailer", "Trailer"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletetrailer.confirm", "Are you sure you want to delete this trailer?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(trailerId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const addCapabilities = useCallback((trailerId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _addCapabilities({trailerId, data}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_addCapabilities, handleUpdateResult])

  const removeCapabilities = useCallback((trailerId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _removeCapabilities({trailerId, data}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_removeCapabilities, handleUpdateResult])

  return {
    createTrailer: createTrailer,
    updateTrailer: updateTrailer,
    deleteTrailer: deleteTrailer,
    addCapabilities: addCapabilities,
    removeCapabilities: removeCapabilities,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
